import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Confirm, Input, Segment, Dimmer, Loader } from 'semantic-ui-react';
import { SemanticToastContainer, toast } from 'react-semantic-toasts';
import { useProducts } from '../../../../providers/ProductProvider';
import { useProposals } from '../../../../providers/ProposalProvider';
import { ProposalFormRow } from './ProposalFormRow';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import { Editor } from 'react-draft-wysiwyg';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { useAuth } from '../../../../providers/AuthenticationProvider';
import { useCallback } from 'react';

const formStyle = {
    border: 'solid black 1px',
    borderRadius: '8px',
    padding: '16px',
    paddingBottom: '16px',
    margin: '16px',
};

const textSectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
};

const labelStyle = {
    fontWeight: 700
};

const editorStyle = {
    border: '2px solid #f1f1f1',
    padding: '0px 5px 0px 5px',
    width: '100%',
    marginBottom: '2.5%'
};

const buttonSection = {
    display: 'flex',
    justifyContent: 'space-between'
};

const rightButton = {
    float: 'right',
    marginRight: '8px',
    display: 'initial'
}

const toastSection = {
    marginTop: '1%'
};

const nameRow = {
    display: 'flex',
    justifyContent: 'space-between'
};

const getReturnRow = (rowData, index, disabled) => {
    return (
        <ProposalFormRow key={index} index={index} id={rowData.id} data={rowData} disabled={disabled} />
    );
};

export const ProposalForm = ({ onSubmit, data, proposalId, loading }) => {
    const navigate = useNavigate();
    const {
        proposalInput,
        openingEditorState,
        termsAndConditionsEditorState,
        addRow,
        createProposal,
        editProposal,
        deleteProposal,
        setProposalInput,
        sendProposal,
        setOpeningEditorState,
        setTermsAndConditionsEditorState,
        name,
        setName,
        customerData,
        setCustomerData,
        downloadProposal,
        proposalSaving
    } = useProposals();
    const [rowObjects, setRowObjects] = useState([]);
    const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [grandTotal, setGrandTotal] = useState(0);
    const [proposalStatus, setProposalStatus] = useState('');
    const [viewToken, setViewToken] = useState();
    const [initialState, setInitalState] = useState();
    const [edited, setEdited] = useState(false);
    const { loadProducts, dropdownProductData } = useProducts();
    const { isSystemAdmin } = useAuth();

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    useEffect(() => {
        if (data) {
            const state = {};
            if(data.proposalItems?.length > 0)
            {
                setProposalInput(data.proposalItems);
                state.proposalInput = data.proposalItems;
            }
            setName(data.name);
            state.name = data.name;
            setCustomerData(data.customer);
            setViewToken(data?.viewToken);
            if (data?.envelope?.status) {
                setProposalStatus(data?.envelope?.status);
            }
            if (data.opening) {
                setOpeningEditorState(EditorState.createWithContent(ContentState.createFromBlockArray(htmlToDraft(data.opening))));
                state.opening = data.opening;
            } else {
                state.opening = EditorState.createEmpty();
            }
            if (data.termsAndConditions) {
                setTermsAndConditionsEditorState(EditorState.createWithContent(ContentState.createFromBlockArray(htmlToDraft(data.termsAndConditions))));
                state.termsAndConditions = data.termsAndConditions;
            } else {
                state.termsAndConditions = EditorState.createEmpty();
            }
            setInitalState(state);
        }
    }, [data, setProposalInput, setOpeningEditorState, setTermsAndConditionsEditorState, setName, setCustomerData]);

    useEffect(() => {
        if (proposalInput) {
            setRowObjects(
                proposalInput.map((rowData, index) => {
                    return getReturnRow(rowData, index, (proposalStatus === 'completed'));
                })
            );
            setGrandTotal(proposalInput.map(item => {
                return ((dropdownProductData.find(x => x.key === item.productId)?.price) * item.quantity)
            }).reduce((prev, curr) => prev + curr));
        } else {
            setGrandTotal(0);
        }
    }, [proposalInput, data, dropdownProductData, proposalStatus]);

    useEffect(() => {
        if (initialState && initialState.name === name && initialState.proposalInput === proposalInput && initialState.opening === draftToHtml(convertToRaw(openingEditorState.getCurrentContent())) && initialState.termsAndConditions === draftToHtml(convertToRaw(termsAndConditionsEditorState.getCurrentContent()))) {
            setEdited(false);
        } else {
            setEdited(true);
        }
    }, [name, proposalInput, openingEditorState, termsAndConditionsEditorState, initialState]);

    const handleEdit = useCallback(() => {
        editProposal(proposalId);
        const state = {
            proposalInput,
            name,
            opening: draftToHtml(convertToRaw(openingEditorState.getCurrentContent())),
            termsAndConditions: draftToHtml(convertToRaw(termsAndConditionsEditorState.getCurrentContent()))
        }
        setInitalState(state);
    }, [editProposal, proposalId, proposalInput, name, openingEditorState, termsAndConditionsEditorState]);

    const handleConfirm = async () => {
        try {
            sendProposal(proposalId);
            toast({
                type: 'success',
                description: 'Email sent successfully.',
                animation: 'fade down',
                time: 5000
            });
        } catch (err) {
            toast({
                type: 'error',
                description: 'Email failed to send, please try again later.',
                animation: 'fade down',
                time: 5000
            });
        }
        setShowEmailConfirmation(false);
    };

    const handleDeleteConfirm = async () => {
        try {
            deleteProposal(proposalId);
        } catch (err) {
            console.log(err);
        }
        navigate('/proposals');
    };

    const viewProposal = async () => {
        window.location.href = window.location.origin + `/proposals/view/${viewToken}`;
    };

    return (
        <Segment style={formStyle}>
            <Dimmer active={loading || !dropdownProductData.length} inverted>
                <Loader active={loading || !dropdownProductData.length} size="massive" inline />
            </Dimmer>
            <Confirm
                open={showEmailConfirmation}
                onCancel={() => setShowEmailConfirmation(false)}
                onConfirm={handleConfirm}
                content={`An email will be sent to ${customerData?.email}. Please press confirm to send the email.`}
                confirmButton="Confirm"
            />
            <Confirm
                open={showDeleteConfirmation}
                onCancel={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDeleteConfirm}
                content={`You are attempting to delete this Proposal are you sure? Please press confirm to delete.`}
                confirmButton="Confirm"
            />
            <Form onSubmit={onSubmit}>
                <Form.Group style={nameRow}>
                    <Form.Field
                        label={'Name'}
                        readOnly={proposalStatus === 'completed'}
                        control={Input}
                        value={name}
                        style={{ height: '38px' }}
                        width="6"
                        onChange={(e, { value }) => {
                            setName(value)
                        }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div>
                            Proposal For: {customerData?.firstname} {customerData?.lastname}, {customerData?.companyName}
                        </div>
                        <div>
                            Proposal Status: {proposalStatus.toUpperCase() ?? 'NONE'}
                        </div>
                    </div>
                </Form.Group>
                {rowObjects}
            </Form>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {
                    grandTotal > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginRight: '1em' }}>
                            <h3>
                                Grand Total: {grandTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </h3>
                        </div>
                    ) : null
                }
                {
                    grandTotal > 0 && customerData?.distributor ? (
                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', marginRight: '1em' }}>
                            <h3>
                                Distributor Total: {(grandTotal * customerData?.distributor?.pricingMultiplier).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </h3>
                        </div>
                    ) : null
                }
                <Button
                    style={{
                        float: 'right',
                        marginRight: '0px',
                        display: 'initial'
                    }}
                    disabled={proposalStatus === 'completed'}
                    onClick={() => {
                        addRow();
                    }}
                >
                    Add Line Item
                </Button>
            </div>
            <div style={textSectionStyle}>
                <label style={labelStyle}>Opening Section</label>
                <div>
                    <Editor
                        editorState={openingEditorState}
                        editorStyle={editorStyle}
                        onEditorStateChange={setOpeningEditorState}
                        readOnly={proposalStatus === 'completed'}
                    />
                </div>
                <label style={labelStyle}>Terms and Conditions Section</label>
                <div>
                    <Editor
                        editorState={termsAndConditionsEditorState}
                        editorStyle={editorStyle}
                        onEditorStateChange={setTermsAndConditionsEditorState}
                        readOnly={proposalStatus === 'completed'}
                    />
                </div>
            </div>
            <div style={buttonSection}>
                <div>
                    <Button
                        style={{
                            float: 'left',
                            marginRight: '8px',
                            display: (data?.id && data?.envelope?.status !== 'completed') ? 'initial' : 'none'
                        }}
                        onClick={() => {
                            setShowEmailConfirmation(true);
                        }}
                    >
                        Send Proposal
                    </Button>
                    <Button
                        style={{
                            float: 'left',
                            marginRight: '8px',
                            display: (data?.id) ? 'initial' : 'none'
                        }}
                        onClick={() => {
                            downloadProposal(data?.id);
                        }}
                    >
                        Download Proposal
                    </Button>
                    <Button
                        style={{
                            float: 'left',
                            marginRight: '8px',
                            display: (data?.id && viewToken) ? 'initial' : 'none'
                        }}
                        onClick={() => {
                            viewProposal();
                        }}
                    >
                        View Proposal
                    </Button>
                </div>
                <div>
                    {isSystemAdmin() ? (
                        <Button
                            style={rightButton}
                            onClick={() => {
                                setShowDeleteConfirmation(true);

                            }}
                            negative
                        >
                            Delete
                        </Button>

                    ) : null}
                    <Button
                        style={rightButton}
                        onClick={() => {
                            navigate('/proposals');
                        }}
                        disabled={proposalSaving}
                    >
                        {proposalId ? 'Close' : 'Cancel'}
                    </Button>
                    {proposalId ? (
                        <Button
                            style={rightButton}
                            disabled={proposalSaving || proposalStatus === 'completed' || !edited}
                            onClick={() => {
                                handleEdit();
                            }}
                        >
                            Save
                        </Button>
                    ) : (
                        <Button
                            style={rightButton}
                            onClick={() => {
                                createProposal();
                            }}
                            disabled={proposalSaving || !edited}
                        >
                            Save
                        </Button>
                    )}
                </div>
            </div>
            <div style={toastSection}>
                <SemanticToastContainer position="bottom-center" />
            </div>
        </Segment>
    );
};
